<template>
    <div class="mr:fixed mr:w-screen mr:h-screen mr:z-(--modal-z-index) mr:top-0 mr:left-0 mr:text-base-grey-5">
        <div
            class="mr:fixed mr:w-full mr:h-full mr:bg-black/65"
            data-cy="mr-popup-overlay"
        />

        <div
            :class="['mr:absolute mr:flex mr:flex-col mr:rounded mr:bg-white mr:max-h-screen-height', popupWrapperClass[variant], contentClass]"
            data-cy="mr-popup-wrapper"
        >
            <div
                class="mr:py-12 mr:px-15 mr:text-center"
                :class="popupHeaderClass[variant]"
                data-cy="mr-popup-header"
            >
                <button
                    class="mr:group mr:absolute mr:right-2 mr:top-2 mr:md:top-5 mr:md:right-5 mr:h-8 mr:w-8 mr:flex mr:justify-center mr:items-center mr:rounded-full mr:close mr:cursor-pointer"
                    :class="popupCloseButtonClass[variant]"
                    data-cy="mr-popup-close"
                    @click="$emit('close')"
                >
                    <img
                        :src="getIconUrl(getCloseIconPath)"
                        :alt="lang('popupAltTextCloseButton')"
                        :class="popupCloseIconClass[variant]"
                        class="mr:w-5 mr:h-5"
                    >
                </button>

                <div
                    class="mr:font-omnes-medium mr:text-24 mr:md:text-32"
                    data-cy="mr-popup-title"
                >
                    <slot name="title">
                        {{ title }}
                    </slot>
                </div>
            </div>

            <div
                :class="[
                    'mr:flex-1 mr:flex mr:flex-col mr:overflow-hidden mr:pt-10 mr:pb-12',
                ]"
                data-cy="mr-popup-body-wrapper"
            >
                <div
                    v-if="hasSlot('summary')"
                    data-cy="mr-popup-summary"
                    :class="[
                        'mr:font-medium mr:flex mr:justify-between mr:pb-6 mr:font-omnes-medium',
                        summaryClass
                    ]"
                >
                    <slot name="summary" />
                </div>

                <div
                    v-if="hasSlot('body')"
                    :class="[
                        'mr:flex mr:grow mr:flex-col mr:overflow-y-auto mr:overflow-x-hidden mr:pb-8 mr:md:pb-10',
                        !props.noPadding ? 'mr:px-8 mr:md:px-20' : ''
                    ]"
                    data-cy="mr-popup-body"
                >
                    <slot name="body" />
                </div>

                <div
                    v-if="hasSlot('footer')"
                    :class="['mr:text-center', !props.noPadding ? 'mr:px-20' : '']"
                    data-cy="mr-popup-footer"
                >
                    <slot name="footer" />
                </div>

                <div
                    v-if="hasSlot('actions')"
                    :class="['mr:text-center', !props.noPadding ? 'mr:px-20' : '']"
                >
                    <div
                        data-cy="mr-popup-actions"
                        class="mr:flex mr:gap-4 mr:justify-center"
                    >
                        <slot name="actions" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { lang } from '@comp/Filters'
import useImage from '@composables/useImage'
import { computed, defineEmits, useSlots } from 'vue'

interface Props {
    noPadding?: boolean
    title?: string
    width?: 'auto' | 'regular' | 'wide' | 'full-screen'
    alignText?: 'left' | 'center' | 'right'
    variant?: 'primary' | 'web-portal'
}

defineEmits(['close'])
const props = withDefaults(defineProps<Props>(), {
    noPadding: false,
    title: '',
    width: 'regular',
    alignText: 'left',
    variant: 'primary'
})

const { getIconUrl } = useImage()
const slots = useSlots()
const hasSlot = (name: string) => !!slots[name]

const contentClass = computed(() => {
    let contentClass = 'mr:top-1/2 mr:left-1/2 mr:-translate-x-1/2 mr:-translate-y-1/2'

    switch (props.alignText) {
        case 'left':
            contentClass += ' mr:text-left'
            break
        case 'center':
            contentClass += ' mr:text-center'
            break
        case 'right':
            contentClass += ' mr:text-right'
            break
    }

    switch (props.width) {
        case 'wide':
            return `${contentClass} mr:w-modal-wide`
        case 'auto':
            return `${contentClass} mr:max-w-9/10`
        case 'full-screen':
            return `${contentClass} mr:w-9/10`
        default:
            return `${contentClass} mr:w-modal-regular`
    }
})

const summaryClass = computed(() => {
    let summaryClass = !props.noPadding ? 'mr:px-6 mr:md:px-20' : ''

    if (props.width === 'full-screen') {
        summaryClass = 'mr:px-8'
    }

    return summaryClass
})

const getCloseIconPath = 'icon-cross.svg'

// classes depends on variant
const popupWrapperClass = {
    'primary': '',
    'web-portal': 'mr:max-w-modal-max overflow-hidden'
}

const popupHeaderClass = {
    'primary': 'mr:bg-base-grey-5/10 mr:text-base-grey-5',
    'web-portal': 'mr:bg-base-grey-5 mr:text-white'
}

const popupCloseButtonClass = {
    'primary': 'mr:bg-base-grey-5/25 mr:hover:bg-base-grey-5',
    'web-portal': 'mr:bg-white/50 mr:hover:bg-white'
}

const popupCloseIconClass = {
    'primary': 'mr:filter-(--base-grey-05-filter) mr:group-hover:filter-(--white-filter)',
    'web-portal': 'mr:filter-(--base-grey-05-filter)'
}
</script>
